import { db } from '@/lib/db/index';
import type { SyncOperation, SyncOpType } from '@/lib/db/index';

export type { SyncOperation, SyncOpType };

// ─── Enqueue ──────────────────────────────────────────────────────────────────

export async function enqueue(
  op: Omit<SyncOperation, 'id'>,
): Promise<void> {
  await db.sync_queue.add({
    ...op,
    id: crypto.randomUUID(),
  });
}

// ─── Read ─────────────────────────────────────────────────────────────────────

/** Returns all pending operations for an org, sorted oldest-first (FIFO). */
export async function getPending(orgId: string): Promise<SyncOperation[]> {
  const ops = await db.sync_queue
    .where('[org_id+status]')
    .equals([orgId, 'pending'])
    .toArray();
  return ops.sort((a, b) => a.created_at.localeCompare(b.created_at));
}

export async function getPendingCount(orgId: string): Promise<number> {
  return db.sync_queue
    .where('[org_id+status]')
    .equals([orgId, 'pending'])
    .count();
}

export async function getFailedCount(orgId: string): Promise<number> {
  return db.sync_queue
    .where('[org_id+status]')
    .equals([orgId, 'failed'])
    .count();
}

// ─── Status updates ───────────────────────────────────────────────────────────

export async function markSyncing(id: string): Promise<void> {
  await db.sync_queue.update(id, { status: 'syncing' });
}

export async function markSynced(id: string): Promise<void> {
  await db.sync_queue.update(id, { status: 'synced', error: undefined });
}

export async function markFailed(id: string, error: string): Promise<void> {
  await db.sync_queue.update(id, { status: 'failed', error });
}

export async function resetToPending(id: string): Promise<void> {
  await db.sync_queue.update(id, { status: 'pending' });
}

export async function incrementRetry(id: string): Promise<number> {
  const op = await db.sync_queue.get(id);
  const next = (op?.retry_count ?? 0) + 1;
  await db.sync_queue.update(id, { retry_count: next });
  return next;
}
